/**
 * CRDT-based storage adapter that wraps Yjs documents
 * Provides a simple key-value interface backed by encrypted sync
 */

import * as Y from 'yjs';
import {
  openDocument,
  resolveRemoteDocumentId,
  syncToRemote,
  storeDocumentMetadata,
  getDocumentMetadata,
  cojoinLocalStorageIntoYDoc,
  sanitizeStorageMap,
  type SyncedDocument,
  type DocumentMetadata,
} from '@yada/sync';

/**
 * Storage interface compatible with existing apps (like habits)
 */
export interface Storage {
  get<T>(key: string): Promise<T | null>;
  set<T>(key: string, value: T): Promise<void>;
}

/**
 * Metadata extractor function - apps provide this to extract depersonalized metadata
 *
 * @param data - Full document data (decrypted)
 * @returns Minimal metadata (counts, types, timestamps only - no personal content)
 */
export type MetadataExtractor = (data: any) => {
  documentType: string; // e.g., 'note', 'task', 'habit'
  itemCount: number; // e.g., 3 notes, 5 tasks
};

/** Local persistence for a document could not be opened or created; nothing was saved. */
export class LocalStorageUnavailableError extends Error {
  constructor(readonly documentId: string, readonly reason: unknown) {
    super(`Local storage for ${documentId} is unavailable: ${reason instanceof Error ? reason.message : String(reason)}`);
    this.name = 'LocalStorageUnavailableError';
  }
}

/**
 * CRDT storage implementation using Yjs
 */
export class CRDTStorage implements Storage {
  private syncedDoc: SyncedDocument | null = null;
  private map: Y.Map<any> | null = null;
  private documentId: string;
  private metadataExtractor: MetadataExtractor | null = null;
  private initializingPromise: Promise<void> | null = null;

  /** Whether this storage is locked due to decryption failure */
  locked = false;
  /** Reason the storage is locked */
  lockReason: string | null = null;

  private lockListeners = new Set<() => void>();
  private unlockListeners = new Set<() => void>();
  private remoteUpdateListeners = new Set<() => void>();
  private seedEventHandler: ((e: Event) => void) | null = null;

  /**
   * Subscribe to lock state changes.
   * The callback fires once when locked transitions false → true.
   * Returns an unsubscribe function.
   */
  onLock(callback: () => void): () => void {
    this.lockListeners.add(callback);
    return () => this.lockListeners.delete(callback);
  }

  /**
   * Subscribe to unlock events (P2P seed delivery → real-time unlock).
   * The callback fires when the storage transitions locked → unlocked.
   * Returns an unsubscribe function.
   */
  onUnlock(callback: () => void): () => void {
    this.unlockListeners.add(callback);
    return () => this.unlockListeners.delete(callback);
  }

  /**
   * Subscribe to remote document updates.
   * Fires whenever another device's changes are merged into the local Yjs doc
   * (origin 'sync:remote' from syncFromRemote). Use this to reload UI state
   * after cross-device edits arrive.
   * Returns an unsubscribe function.
   */
  onRemoteUpdate(callback: () => void): () => void {
    this.remoteUpdateListeners.add(callback);
    return () => this.remoteUpdateListeners.delete(callback);
  }

  private triggerLock(reason: string): void {
    this.locked = true;
    this.lockReason = reason;
    this.lockListeners.forEach((cb) => {
      try { cb(); } catch (err) {
        console.error('[CRDTStorage] Lock listener error:', err);
      }
    });

    // Listen for P2P seed delivery so we can unlock in real-time
    if (typeof window !== 'undefined' && !this.seedEventHandler) {
      this.seedEventHandler = (e: Event) => {
        const detail = (e as CustomEvent<{ workspaceId: string }>).detail;
        if (detail?.workspaceId === this.workspaceId) {
          this.tryUnlock();
        }
      };
      window.addEventListener('yada:workspace:seed-received', this.seedEventHandler);
    }
  }

  /** Retry initialization after a seed arrives (P2P or server fallback). */
  private tryUnlock(): void {
    if (!this.locked) return;
    console.log(`[CRDTStorage] Retrying unlock for ${this.documentId} after seed delivery`);
    this.locked = false;
    this.lockReason = null;
    this.syncedDoc = null;
    this.map = null;
    this.initializingPromise = null;

    if (typeof window !== 'undefined' && this.seedEventHandler) {
      window.removeEventListener('yada:workspace:seed-received', this.seedEventHandler);
      this.seedEventHandler = null;
    }

    this.initialize().then(() => {
      if (!this.locked) {
        console.log(`[CRDTStorage] Unlocked ${this.documentId} via P2P seed delivery`);
        this.unlockListeners.forEach(cb => {
          try { cb(); } catch { /* ignore */ }
        });
      }
    }).catch(err => {
      console.warn(`[CRDTStorage] Unlock retry failed for ${this.documentId}:`, err);
    });
  }

  constructor(
    private workspaceId: string,
    documentId: string,
    private docType: string,
    private appId: string,
    metadataExtractor?: MetadataExtractor,
    private legacyDocumentId?: string
  ) {
    this.documentId = documentId;
    this.metadataExtractor = metadataExtractor || null;
  }

  /**
   * Initialize the Yjs document
   */
  async initialize(): Promise<void> {
    if (this.syncedDoc) return;
    if (this.locked) return;
    if (this.initializingPromise) return this.initializingPromise;

    this.initializingPromise = this._doInitialize().finally(() => {
      this.initializingPromise = null;
    });
    return this.initializingPromise;
  }

  private async _doInitialize(): Promise<void> {
    if (this.syncedDoc) return;

    try {
      this.documentId = await resolveRemoteDocumentId(this.documentId, this.legacyDocumentId, this.workspaceId);
    } catch {
      // Non-blocking discovery fallback
    }

    try {
      this.syncedDoc = await openDocument(this.workspaceId, this.documentId, this.docType);
    } catch (err) {
      // openDocument already handles a document that does not exist yet. A
      // replacement under the same id would start from empty state while the
      // real document is unreadable, so report the store as unavailable.
      throw new LocalStorageUnavailableError(this.documentId, err);
    }

    if (this.syncedDoc) {
      this.map = this.syncedDoc.doc.getMap('storage');

      try {
        const hadLocal = cojoinLocalStorageIntoYDoc(this.appId, this.syncedDoc.doc);
        sanitizeStorageMap(this.appId, this.map);
        if (hadLocal) {
          syncToRemote(this.syncedDoc).catch(err =>
            console.warn(`[CRDTStorage] Failed to push cojoined doc ${this.documentId} to server:`, err)
          );
        }
      } catch (e) {
        console.warn(`[CRDTStorage] Reconciliation warning for ${this.appId}:`, e);
      }

      this.syncedDoc.doc.on('update', (_update: Uint8Array, origin: unknown) => {
        if (origin === 'sync:remote') {
          try {
            if (this.map) {
              sanitizeStorageMap(this.appId, this.map);
            }
          } catch {}

          if (this.remoteUpdateListeners.size > 0) {
            this.remoteUpdateListeners.forEach(cb => {
              try { cb(); } catch { /* ignore listener errors */ }
            });
          }
        }
      });
    }
  }

  /**
   * Get value by key - always serves local data if available
   */
  async get<T>(key: string): Promise<T | null> {
    if (!this.map) {
      await this.initialize();
    }

    if (!this.map) return null;

    const value = this.map.get(key);
    return value instanceof Y.Text ? value.toString() as T : value !== undefined ? value : null;
  }

  /**
   * Set value by key - writes to local Yjs map and triggers local persistence immediately
   */
  async set<T>(key: string, value: T): Promise<void> {
    if (!this.map) {
      await this.initialize();
    }

    if (!this.map) return;

    // Docs bodies must be edited as text. Replacing a scalar Y.Map value makes
    // simultaneous offline edits last-writer-wins, even though Yjs merges both
    // updates successfully. Keep the public storage API string-based.
    const node = this.appId === '@yada/app-docs' && key.startsWith('doc:')
      ? this.map.get(`node:${key.slice(4)}`) as { docType?: string } | undefined
      : undefined;
    if (node?.docType === 'note' && typeof value === 'string') {
      let text = this.map.get(key);
      if (!(text instanceof Y.Text)) {
        const previous = typeof text === 'string' ? text : '';
        text = new Y.Text(previous);
        this.map.set(key, text);
      }
      const current = text.toString();
      const next = value;
      let prefix = 0;
      while (prefix < current.length && prefix < next.length && current[prefix] === next[prefix]) prefix++;
      let suffix = 0;
      while (suffix < current.length - prefix && suffix < next.length - prefix &&
        current[current.length - 1 - suffix] === next[next.length - 1 - suffix]) suffix++;
      if (prefix !== current.length || prefix !== next.length) {
        this.map.doc?.transact(() => {
          if (current.length - prefix - suffix) text.delete(prefix, current.length - prefix - suffix);
          if (next.length - prefix - suffix) text.insert(prefix, next.slice(prefix, next.length - suffix));
        });
      }
    } else {
      this.map.set(key, value);
    }

    this.updateMetadata().catch((err) =>
      console.warn('[CRDTStorage] Metadata update failed:', err)
    );
  }

  /**
   * Remove value by key
   */
  async remove(key: string): Promise<void> {
    if (!this.map) {
      await this.initialize();
    }

    if (!this.map) return;

    this.map.delete(key);

    this.updateMetadata().catch((err) =>
      console.warn('[CRDTStorage] Metadata update failed:', err)
    );
  }

  /**
   * Return all active keys in storage
   */
  async keys(): Promise<string[]> {
    if (!this.map) {
      await this.initialize();
    }

    if (!this.map) return [];

    return Array.from(this.map.keys()).filter((k: string) => {
      const v = this.map!.get(k);
      return v !== null && v !== undefined;
    });
  }

  /**
   * Get the underlying Yjs document
   */
  getDocument(): SyncedDocument | null {
    return this.syncedDoc;
  }

  /**
   * Update depersonalized metadata (called after document changes)
   */
  private async updateMetadata(): Promise<void> {
    if (!this.metadataExtractor || this.locked) return;

    try {
      const data: Record<string, any> = {};
      if (this.map) {
        this.map.forEach((value: any, key: string) => {
          data[key] = value instanceof Y.Text ? value.toString() : value;
        });
      }

      const extracted = this.metadataExtractor(data);

      await storeDocumentMetadata({
        documentId: this.documentId,
        workspaceId: this.workspaceId,
        appId: this.appId,
        documentType: extracted.documentType,
        itemCount: extracted.itemCount,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      });
    } catch (err) {
      console.warn('[CRDTStorage] Failed to update metadata:', err);
    }
  }

  /**
   * Get depersonalized metadata (works even when locked)
   */
  async getMetadata(): Promise<DocumentMetadata | null> {
    return await getDocumentMetadata(this.documentId);
  }

  /**
   * Manually trigger metadata update
   */
  async syncMetadata(): Promise<void> {
    await this.updateMetadata();
  }
}

/**
 * Create CRDT storage for an app
 */
export async function createCRDTStorage(
  workspaceId: string,
  appId: string,
  metadataExtractor?: MetadataExtractor
): Promise<CRDTStorage> {
  const { getStorageDocumentId, getLegacyStorageDocumentId } = await import('./documentId');
  const documentId = getStorageDocumentId(appId, workspaceId);
  const legacyDocumentId = getLegacyStorageDocumentId(appId, workspaceId);
  const storage = new CRDTStorage(workspaceId, documentId, appId, appId, metadataExtractor, legacyDocumentId);
  await storage.initialize();
  return storage;
}
